import type { CSSProperties, ReactNode } from "react";
import {
  formatDashboardCount,
  formatDashboardCurrency,
  formatDashboardUnitQuantity,
} from "@/features/dashboard/metricFormatters";
import type {
  DashboardDetailKind,
  DashboardDetailLoadState,
  DashboardFinancialDetailItem,
  DashboardFinancialLoadLine,
  DashboardLoadLine,
  DashboardOperationalDetailItem,
  DashboardUnitQuantity,
} from "@/features/dashboard/types";
import {
  dashboardLabelStyle,
  dashboardMonoValueStyle,
} from "./dashboardStyles";

export type DashboardDetailItem =
  | DashboardFinancialDetailItem
  | DashboardOperationalDetailItem
  | DashboardLoadLine;

export type DashboardDetailPresentation = "financial" | "operational";

interface DashboardDetailItemsProps {
  items: DashboardDetailItem[];
  entityKind?: DashboardDetailKind;
  presentation: DashboardDetailPresentation;
}

export function DashboardDetailItems({
  items,
  entityKind,
  presentation,
}: DashboardDetailItemsProps) {
  return (
    <div
      aria-label="Registros detalhados"
      style={{ display: "grid", gap: "0.625rem", minWidth: 0 }}
    >
      {items.map((item, index) => (
        <DetailItem
          key={detailItemKey(item, index)}
          item={item}
          entityKind={entityKind}
          presentation={presentation}
        />
      ))}
    </div>
  );
}

const detailCardStyle: CSSProperties = {
  display: "grid",
  gap: "0.65rem",
  minWidth: 0,
  maxWidth: "100%",
  padding: "0.875rem",
  border: "1px solid var(--login-border)",
  borderRadius: "0.625rem",
  background: "var(--login-bg)",
  color: "var(--login-text)",
};

function detailItemKey(item: DashboardDetailItem, index: number) {
  return "line_id" in item
    ? `${item.line_id}-${index}`
    : `${item.order_id}-${index}`;
}

function DetailItem({
  item,
  entityKind,
  presentation,
}: {
  item: DashboardDetailItem;
  entityKind?: DashboardDetailKind;
  presentation: DashboardDetailPresentation;
}) {
  if (entityKind === "load" && "line_id" in item) {
    return <LoadDetailItem item={item} presentation={presentation} />;
  }
  if (entityKind === "checker" && "checked_items" in item) {
    return <OperationalDetailItem item={item} />;
  }
  if (
    presentation === "financial" &&
    "label" in item &&
    "value_cents" in item
  ) {
    return <FinancialDetailItem item={item} />;
  }
  return null;
}

function FinancialDetailItem({ item }: { item: DashboardFinancialDetailItem }) {
  return (
    <article
      data-testid={`dashboard-detail-item-${item.order_id}`}
      style={detailCardStyle}
    >
      <ItemTitle title={item.label} />
      <MetricGrid>
        <Metric
          label="Data"
          value={formatBusinessDate(item.business_date)}
          exact
        />
        <Metric label="Situação" value={detailStateLabel(item.state)} />
        <Metric
          label="Valor"
          value={formatDashboardCurrency(item.value_cents).full}
          exact
        />
      </MetricGrid>
      <QuantityList quantities={item.quantities} label="Quantidades" />
    </article>
  );
}

function OperationalDetailItem({
  item,
}: {
  item: DashboardOperationalDetailItem;
}) {
  const divergences = formatDashboardCount(item.divergences).full;
  return (
    <article
      data-testid={`dashboard-detail-item-${item.order_id}`}
      style={detailCardStyle}
    >
      <ItemTitle title={`Carga ${item.order_id}`} />
      <MetricGrid>
        <Metric
          label="Data"
          value={formatBusinessDate(item.business_date)}
          exact
        />
        <Metric label="Situação" value={detailStateLabel(item.state)} />
        <Metric
          label="Itens conferidos"
          value={formatDashboardCount(item.checked_items).full}
          exact
        />
        <Metric
          label="Divergências"
          value={`${divergences} ${item.divergences === 1 ? "divergência" : "divergências"}`}
          exact
        />
      </MetricGrid>
      <QuantityList
        quantities={item.checked_quantities}
        label="Quantidades conferidas"
      />
    </article>
  );
}

function isFinancialLoadLine(
  item: DashboardLoadLine,
): item is DashboardFinancialLoadLine {
  return (
    "supplier_id" in item &&
    "supplier_name" in item &&
    "unit_price_cents" in item &&
    "value_cents" in item
  );
}

function LoadDetailItem({
  item,
  presentation,
}: {
  item: DashboardLoadLine;
  presentation: DashboardDetailPresentation;
}) {
  const financial = presentation === "financial" && isFinancialLoadLine(item);
  const expected =
    formatDashboardUnitQuantity(item.unit, item.expected_quantity)?.full ??
    "Indisponível";
  const received =
    formatDashboardUnitQuantity(item.unit, item.received_quantity)?.full ??
    "Indisponível";
  return (
    <article
      data-testid={`dashboard-detail-item-${item.line_id}`}
      style={detailCardStyle}
    >
      <ItemTitle title={item.product_name} />
      {financial ? (
        <p
          title={item.supplier_name}
          style={{
            margin: "-0.25rem 0 0",
            minWidth: 0,
            color: "var(--dashboard-body-secondary)",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "0.82rem",
            overflowWrap: "anywhere",
          }}
        >
          {item.supplier_name}
        </p>
      ) : null}
      <MetricGrid>
        <Metric label="Esperado" value={expected} unit={item.unit} exact />
        <Metric label="Recebido" value={received} unit={item.unit} exact />
        <Metric label="Situação" value={loadLineStatusLabel(item.status)} />
        {financial ? (
          <>
            <Metric
              label="Preço unitário"
              value={formatDashboardCurrency(item.unit_price_cents).full}
              exact
            />
            <Metric
              label="Valor"
              value={formatDashboardCurrency(item.value_cents).full}
              exact
            />
          </>
        ) : null}
      </MetricGrid>
    </article>
  );
}

function ItemTitle({ title }: { title: string }) {
  return (
    <h4
      title={title}
      style={{
        margin: 0,
        minWidth: 0,
        color: "var(--login-text)",
        fontFamily: "'DM Sans', sans-serif",
        fontSize: "0.92rem",
        fontWeight: 700,
        lineHeight: 1.3,
        overflowWrap: "anywhere",
      }}
    >
      {title}
    </h4>
  );
}

function MetricGrid({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 8rem), 1fr))",
        gap: "0.625rem",
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      {children}
    </div>
  );
}

function Metric({
  label,
  value,
  unit,
  exact = false,
}: {
  label: string;
  value: string;
  unit?: string;
  exact?: boolean;
}) {
  return (
    <div style={{ display: "grid", gap: "0.2rem", minWidth: 0 }}>
      <span style={dashboardLabelStyle}>{label}</span>
      <span
        title={`${value}${unit ? ` ${unit}` : ""}`}
        style={{
          ...dashboardMonoValueStyle,
          ...(exact ? exactValueStyle : undefined),
          fontSize: "0.9rem",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
        {unit ? (
          <small
            style={{
              marginLeft: "0.3rem",
              color: "var(--dashboard-body-secondary)",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "0.72rem",
              fontWeight: 700,
            }}
          >
            {unit}
          </small>
        ) : null}
      </span>
    </div>
  );
}

const exactValueStyle: CSSProperties = {
  display: "block",
  minWidth: 0,
  maxWidth: "100%",
  overflow: "hidden",
  overflowWrap: "normal",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  wordBreak: "normal",
};

function QuantityList({
  quantities,
  label,
}: {
  quantities: DashboardUnitQuantity[];
  label: string;
}) {
  if (quantities.length === 0) return null;
  return (
    <div
      aria-label={label}
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 7rem), 1fr))",
        gap: "0.5rem",
        minWidth: 0,
      }}
    >
      {quantities.map((quantity, index) => {
        const value =
          formatDashboardUnitQuantity(quantity.unit, quantity.quantity)?.full ??
          "Indisponível";
        return (
          <div
            key={`${quantity.unit}-${quantity.quantity}-${index}`}
            style={{
              display: "grid",
              gridTemplateColumns: "auto minmax(0, 1fr)",
              gap: "0.45rem",
              alignItems: "baseline",
              minWidth: 0,
            }}
          >
            <span
              style={{ ...dashboardLabelStyle, color: "var(--login-accent)" }}
            >
              {quantity.unit}
            </span>
            <span
              title={value}
              style={{
                ...dashboardMonoValueStyle,
                ...exactValueStyle,
                fontSize: "0.88rem",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {value}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function formatBusinessDate(date: string) {
  const [year, month, day] = date.split("-");
  return year && month && day ? `${day}/${month}/${year}` : date;
}

function detailStateLabel(state: DashboardDetailLoadState) {
  switch (state) {
    case "not_started":
      return "Não iniciada";
    case "in_progress":
      return "Em andamento";
    case "done":
      return "Concluída";
    case "unknown":
      return "Estado não informado";
  }
}

function loadLineStatusLabel(status: string) {
  switch (status.toUpperCase()) {
    case "DIVERGENT":
      return "Divergente";
    case "PENDING":
      return "Pendente";
    case "MATCHED":
    case "CHECKED":
      return "Conferido";
    default:
      return "Status não informado";
  }
}
